import clsx, { type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const NF_INT = new Intl.NumberFormat("ru-RU");
const NF_DEC = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 1 });

export const fmtInt = (n: number | null | undefined) =>
  n == null ? "—" : NF_INT.format(Math.round(n));

export const fmtDec = (n: number | null | undefined) =>
  n == null ? "—" : NF_DEC.format(n);

// Сумма в валюте СЕТИ. Валюта приходит из Network.currency — жёсткий доллар
// врал бы на евровых сетках. Незнакомый код печатаем кодом: «120 PLN» честнее
// чужого символа. (В «Финансах» другой случай — там суммы уже сведены в USD.)
const CURRENCY_SIGN: Record<string, string> = { USD: "$", EUR: "€" };
export const fmtCash = (
  amount: number,
  currency: string | null | undefined,
) => {
  const sign = currency ? CURRENCY_SIGN[currency] : undefined;
  if (sign) return `${sign}${fmtInt(amount)}`;
  return currency ? `${fmtInt(amount)} ${currency}` : fmtInt(amount);
};

export const fmtPct = (n: number | null | undefined, frac = 1) =>
  n == null
    ? "—"
    : `${new Intl.NumberFormat("ru-RU", { maximumFractionDigits: frac }).format(n * 100)}%`;

// Percent change of the current month-to-date vs the same range last month.
// Returns null when there's no baseline (last month had 0) so the UI can skip
// the badge instead of showing ∞.
export function monthDelta(
  month: { current: number; previous: number } | undefined | null,
): number | null {
  if (!month || month.previous === 0) return null;
  return ((month.current - month.previous) / month.previous) * 100;
}

export function host(url: string) {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

// Normalize a domain/url to a comparable key: strip protocol, "sc-domain:"
// prefix, "www.", any path, trailing dots; lowercase. Matches a registration's
// sheetTab (e.g. "reliform.com") to a Site url ("https://reliform.com/" or
// "sc-domain:reliform.com").
export function normalizeDomain(input: string): string {
  let s = (input || "").trim().toLowerCase();
  s = s.replace(/^sc-domain:/, "");
  s = s.replace(/^https?:\/\//, "");
  s = s.replace(/^www\./, "");
  s = s.split("/")[0];
  s = s.replace(/\.+$/, "");
  return s;
}

// Current wall-clock time in a timezone, reinterpreted as a UTC instant. Reg
// timestamps are stored as Kyiv wall-clock-as-UTC, so filter ranges must be
// built in that same frame (otherwise the viewer's real-UTC "now" is 2–3h
// behind and recent rows get excluded).
export function zonedNowAsUtc(timeZone: string): Date {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(new Date());
  const get = (t: string) => Number(parts.find((x) => x.type === t)?.value);
  let hour = get("hour");
  if (hour === 24) hour = 0; // some engines emit 24 for midnight
  return new Date(
    Date.UTC(
      get("year"),
      get("month") - 1,
      get("day"),
      hour,
      get("minute"),
      get("second"),
    ),
  );
}

// Registration timestamps are stored as wall-clock-as-UTC (the sheet's Kyiv
// time). Render them in UTC so the displayed value equals the sheet exactly,
// regardless of the viewer's timezone.
export function fmtRegTime(ts: string | null | undefined): string {
  if (!ts) return "—";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("ru-RU", {
    timeZone: "UTC",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Short local date (no time) — used for "дата выпуска" and marker rows.
export function fmtDate(d: string | Date | null | undefined): string {
  if (!d) return "—";
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export function pathOf(url: string) {
  try {
    const u = new URL(url);
    return u.pathname + u.search;
  } catch {
    return url;
  }
}

export function faviconFor(siteUrl: string) {
  const h = host(siteUrl);
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(h)}&sz=64`;
}

export function relativeTime(date: string | Date | null | undefined) {
  if (!date) return "никогда";
  const d = new Date(date);
  const diff = Date.now() - d.getTime();
  const m = Math.round(diff / 60000);
  if (m < 1) return "только что";
  if (m < 60) return `${m} мин назад`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} ч назад`;
  const days = Math.round(h / 24);
  if (days < 30) return `${days} д назад`;
  return d.toLocaleDateString("ru-RU");
}

export { classifyCwv } from "@/lib/cwv";
